import { useMemo, useState } from 'react'
import { Calculator, Sparkles } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import useStore from '@/stores/useStore'
import {
  AGE_MIXES,
  COMP_SPREADS,
  ESTIMATOR_DEFAULTS,
  normalizeEstimatorInputs,
} from '@/lib/estimator'
import { requestEstimateAssist, parseEstimateAssistContent, sanitizeEstimateAssist } from '@/lib/estimatorAi'
import { hasAiConsent, setAiConsent } from '@/lib/aiConsent'
import AiConsentDialog from '@/components/ai/AiConsentDialog'

const FIELD_CLASS = 'h-8 w-full rounded border border-border bg-background px-2 text-sm'

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-foreground/70">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  )
}

// Rough-numbers form for Estimate Mode: what a prospect tells you in a first
// meeting, nothing more. Generates a synthetic census the whole app can run on.
export default function EstimatorDialog({ open, onOpenChange }) {
  const workspaceMode = useStore((state) => state.workspaceMode)
  const currentInputs = useStore((state) => state.estimator?.inputs)
  const enterEstimateMode = useStore((state) => state.enterEstimateMode)

  const editing = workspaceMode === 'estimate' && currentInputs
  const [draft, setDraft] = useState(() => ({ ...ESTIMATOR_DEFAULTS, ...(currentInputs || {}) }))

  // Reseed the form whenever the dialog reopens against fresh store state.
  const [seenOpen, setSeenOpen] = useState(open)
  if (open !== seenOpen) {
    setSeenOpen(open)
    if (open) setDraft({ ...ESTIMATOR_DEFAULTS, ...(useStore.getState().estimator?.inputs || {}) })
  }

  const set = (key) => (event) => setDraft((d) => ({ ...d, [key]: event.target.value }))
  const normalized = useMemo(() => normalizeEstimatorInputs(draft), [draft])

  // Optional AI assist: a free-text description prefills the form; everything
  // stays reviewable and editable before the estimate starts.
  const [description, setDescription] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [aiMessage, setAiMessage] = useState('')
  const [consentOpen, setConsentOpen] = useState(false)

  const fillWithAi = async () => {
    if (!description.trim() || aiBusy) return
    // One-time gate before anything leaves the browser (same promise as Julio).
    if (!hasAiConsent('estimator')) {
      setConsentOpen(true)
      return
    }
    setAiBusy(true)
    setAiMessage('')
    try {
      const content = await requestEstimateAssist(description)
      const { inputs } = sanitizeEstimateAssist(parseEstimateAssistContent(content))
      if (Object.keys(inputs).length === 0) {
        setAiMessage('Add more detail and try again.')
        return
      }
      setDraft((d) => ({ ...d, ...inputs }))
      setAiMessage('Form updated. Change anything.')
    } catch {
      setAiMessage('AI is unavailable. Use the prefilled form.')
    } finally {
      setAiBusy(false)
    }
  }

  const start = () => {
    enterEstimateMode(editing ? { ...draft, seed: currentInputs.seed, anchorYear: currentInputs.anchorYear } : draft)
    onOpenChange(false)
  }

  return (
    <>
    <AiConsentDialog
      open={consentOpen}
      onOpenChange={setConsentOpen}
      title="Use AI to fill the form?"
      description="A quick note on what leaves your browser, before the first time."
      intro={<span><span className="font-semibold">Only the description you typed is sent to the AI</span> – nothing else from the calculator. It comes back as suggested form values you can edit before anything runs.</span>}
      maySend={['The company description you typed in this dialog']}
      neverSend={['Employee data', 'Plan settings', 'Saved clients or files']}
      acceptLabel="Enable and fill the form"
      onAccept={() => {
        setAiConsent('estimator', true)
        setConsentOpen(false)
        fillWithAi()
      }}
    />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-[calc(100vw-2rem)] md:max-w-4xl lg:max-w-6xl"
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            {editing ? 'Edit estimate assumptions' : 'Estimate a plan – no employee file needed'}
          </DialogTitle>
          <DialogDescription>
            Prefilled for you. Change anything.
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)] sm:items-start lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)]">
            <section className="space-y-3" aria-label="Estimate details">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                <Field label="How many employees?">
                  <Input type="number" min="1" max="2000" value={draft.headcount} onChange={set('headcount')} />
                </Field>
                <Field label="Typical pay (annual)">
                  <Input type="number" min="15000" step="5000" value={draft.typicalComp} onChange={set('typicalComp')} />
                </Field>
                <Field label="How spread out is pay?">
                  <select className={FIELD_CLASS} value={draft.compSpread} onChange={set('compSpread')}>
                    {Object.entries(COMP_SPREADS).map(([key, spread]) => (
                      <option key={key} value={key}>{spread.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Age mix">
                  <select className={FIELD_CLASS} value={draft.ageMix} onChange={set('ageMix')}>
                    {Object.entries(AGE_MIXES).map(([key, mix]) => (
                      <option key={key} value={key}>{mix.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Owners / partners">
                  <Input type="number" min="0" max="20" value={draft.ownerCount} onChange={set('ownerCount')} />
                </Field>
                <Field label="Owner pay (each, annual)">
                  <Input type="number" min="15000" step="10000" value={draft.ownerComp} onChange={set('ownerComp')} />
                </Field>
                <Field label="% who would participate" hint="Rough guess is fine">
                  <Input type="number" min="0" max="100" value={draft.participationRate} onChange={set('participationRate')} />
                </Field>
                <Field label="Typical deferral %">
                  <Input type="number" min="0" max="100" step="0.5" value={draft.avgDeferralPercent} onChange={set('avgDeferralPercent')} />
                </Field>
                <Field label="Non-owner high earners" hint="Staff over ~$160k (0 if none)">
                  <Input type="number" min="0" value={draft.hceCount} onChange={set('hceCount')} />
                </Field>
                <Field label="% part-time staff" hint="0 if unknown">
                  <Input type="number" min="0" max="100" value={draft.partTimePercent} onChange={set('partTimePercent')} />
                </Field>
                <Field label="% yearly turnover" hint="0 if unknown">
                  <Input type="number" min="0" max="100" value={draft.turnoverPercent} onChange={set('turnoverPercent')} />
                </Field>
              </div>

              <p className="text-xs text-muted-foreground">
                {normalized.headcount} employees · {normalized.ownerCount} owner{normalized.ownerCount === 1 ? '' : 's'} · Sample data only.
              </p>
            </section>

            <aside className="space-y-3 rounded-md border border-border bg-muted/30 p-3 sm:sticky sm:top-0">
              <div className="space-y-0.5">
                <h3 className="flex items-center gap-1.5 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-primary" /> Or describe the company
                </h3>
                <p className="text-xs text-muted-foreground">AI fills the form.</p>
              </div>
              <textarea
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                  setAiMessage('')
                }}
                rows={7}
                maxLength={4000}
                placeholder="Dental office, 2 partners, 15 staff…"
                aria-label="Describe the company"
                className="w-full resize-y rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
              <Button type="button" variant="outline" size="sm" className="w-full" disabled={!description.trim() || aiBusy} onClick={fillWithAi}>
                {aiBusy ? 'Thinking…' : 'Fill form with AI'}
              </Button>
              {aiMessage && (
                <p className="text-xs text-muted-foreground" role="status">{aiMessage}</p>
              )}
              <p className="text-[11px] text-muted-foreground">Only this description is sent.</p>
            </aside>
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" className="bg-amber-600 text-white hover:bg-amber-700" onClick={start}>
            {editing ? 'Update estimate' : 'Start estimate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
